// ExecutiveSimulationStudio.tsx - Executive Risk-Revenue Optimization Studio (Monte Carlo)
import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, AlertTriangle, Target, FileText, Briefcase, Shield, DollarSign, BarChart3, Activity } from 'lucide-react';
import HelpTooltip from '../../../shared/components/HelpTooltip';
import { executiveRiskEngine, type ExecutiveSimulationResults } from '../../../services/ExecutiveRiskRevenueEngine';
import { authHeaders } from '../../../services/authHeaders';
import BoardReportGenerator from './BoardReportGenerator';
import ThreatScenarioCharts from './ThreatScenarioCharts';
import apiConfig from '../../../config/apiConfig';

interface Props {
  assessmentId?: string;
  companyData?: {
    name: string;
    industry: string;
    annualRevenue: number | null;
    currentCyberBudget: number;
  };
}

const ExecutiveSimulationStudioSimple: React.FC<Props> = ({ assessmentId, companyData }) => {
  console.log('🎯 ExecutiveSimulationStudioSimple mounting with updated navigation styling:', { assessmentId, companyData });
  
  const revenueMeasured = companyData?.annualRevenue !== null && companyData?.annualRevenue !== undefined;
  const [inputs, setInputs] = useState({
    // Seeded at 0 only as an editable-field default, never asserted as a measurement —
    // see annualRevenueUnmeasured, which gates the read-only summary display below.
    annualRevenue: revenueMeasured ? (companyData!.annualRevenue as number) : 0,
    currentCyberBudget: companyData?.currentCyberBudget || 0,
    industry: companyData?.industry || 'General'
  });
  const [annualRevenueUnmeasured, setAnnualRevenueUnmeasured] = useState(!revenueMeasured);

  // Fetch actual cyber budget from assessment to align with Investment Optimization
  useEffect(() => {
    console.log('🔍 useEffect triggered for budget fetch. AssessmentId:', assessmentId);

    const fetchActualBudget = async () => {
      if (assessmentId) {
        try {
          console.log('💰 Fetching actual cyber budget from assessment for alignment...');
          // Gated route (v3.65) — without credentials this 401s and the budget silently
          // stays un-aligned. See services/authHeaders.ts.
          const response = await fetch(
            `${apiConfig.getApiBaseUrl()}/business-intelligence/${assessmentId}`,
            { headers: authHeaders() }
          );
          if (!response.ok) {
            console.warn(
              `⚠️ Budget alignment fetch failed (${response.status}) — keeping the un-aligned budget rather than silently proceeding as if none existed`
            );
          }
          if (response.ok) {
            const data = await response.json();
            console.log('📊 Business Intelligence API response:', {
              hasInvestmentScenarios: !!data.investmentScenarios,
              scenarioCount: data.investmentScenarios?.length,
              scenarios: data.investmentScenarios?.map(s => ({ label: s.label, investment: s.investment }))
            });

            // Backend doesn't return 'success' field, check for investmentScenarios directly
            if (data.investmentScenarios && Array.isArray(data.investmentScenarios)) {
              const currentScenario = data.investmentScenarios.find(s => s.isCurrent || s.label === 'Current Budget');
              if (currentScenario) {
                console.log(`✅ Aligned budget with Investment Optimization: $${(currentScenario.investment/1000000).toFixed(2)}M`);
                setInputs(prev => ({
                  ...prev,
                  currentCyberBudget: currentScenario.investment
                }));
              } else {
                console.warn('⚠️ No current budget scenario found in:', data.investmentScenarios);
              }
            } else {
              console.warn('⚠️ No investmentScenarios found in API response');
            }
          } else {
            console.error(`❌ API returned status ${response.status}`);
          }
        } catch (error) {
          console.error('❌ Error fetching actual budget for alignment:', error);
        }
      }
    };

    fetchActualBudget();
  }, [assessmentId]);

  const [simulationResults, setSimulationResults] = useState<ExecutiveSimulationResults | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [outsidePressures, setOutsidePressures] = useState<any>(null);
  const [prismData, setPrismData] = useState<any>(null);
  const [crsScore, setCrsScore] = useState<number | null>(null);
  const [showBoardReport, setShowBoardReport] = useState(false);

  // Fetch outside pressures and PRISM data when component mounts
  useEffect(() => {
    if (assessmentId) {
      console.log('🌍 Fetching outside pressures for assessment:', assessmentId);
      fetch(`${apiConfig.getApiBaseUrl()}/risk-intelligence/outside-pressures/${assessmentId}`, { headers: authHeaders() })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            console.log('✅ Outside pressures loaded:', data.outsidePressures);
            setOutsidePressures(data.outsidePressures);
          } else {
            console.warn('⚠️ Failed to load outside pressures:', data.error);
          }
        })
        .catch(error => {
          console.error('❌ Error fetching outside pressures:', error);
        });

      // Fetch CRS score for board report badge
      fetch(`${apiConfig.getApiBaseUrl()}/crs/${assessmentId}`, { headers: authHeaders() })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data?.score != null) {
            setCrsScore(data.data.score);
          }
        })
        .catch(() => { /* CRS optional — badge falls back to financial derivation */ });

      // Fetch PRISM data for board report (Phase 5)
      fetch(`${apiConfig.getApiBaseUrl()}/foundations/prism-data/${assessmentId}`, {
        headers: authHeaders(),
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.data?.connected !== false) {
            console.log('✅ PRISM data loaded for board report');
            setPrismData(data.data);
          }
        })
        .catch(() => {
          // PRISM unavailable — board report works without it
        });
    }
  }, [assessmentId]);

  const formatCurrency = (amount: number): string => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    } else {
      return `$${amount.toFixed(0)}`;
    }
  };

  const formatPercent = (decimal: number): string => {
    return `${(decimal * 100).toFixed(1)}%`;
  };

  // Fallback risk factors when assessment data is not available
  const getFallbackRiskFactors = () => {
    console.log('🔄 Using fallback risk factors (no assessment data available)');
    return [
      {
        id: 'governance_fallback',
        name: 'Security Governance Framework',
        category: 'governance' as const,
        currentScore: 65,
        weight: 1.5,
        frequency: 0.14,
        impact: 5,
        confidenceLevel: 0.85
      },
      {
        id: 'tech_debt_fallback',
        name: 'Legacy System Vulnerabilities',
        category: 'technical_debt' as const,
        currentScore: 72,
        weight: 1.8,
        frequency: 0.21,
        impact: 7,
        confidenceLevel: 0.90
      },
      {
        id: 'culture_fallback',
        name: 'Security Awareness Culture',
        category: 'culture' as const,
        currentScore: 58,
        weight: 1.3,
        frequency: 0.24,
        impact: 4,
        confidenceLevel: 0.75
      }
    ];
  };

  const runSimulation = async () => {
    setIsRunning(true);
    
    try {
      console.log('🎲 Starting Executive Monte Carlo simulation...');
      
      let riskFactors = [];
      
      // Fetch dynamic risk factors from assessment responses
      if (assessmentId) {
        try {
          console.log(`📊 Fetching dynamic risk factors for assessment ${assessmentId}`);
          const riskResponse = await fetch(`${apiConfig.getApiBaseUrl()}/assessments/risk-factors/${assessmentId}`, { headers: authHeaders() });
          
          if (riskResponse.ok) {
            const riskData = await riskResponse.json();
            
            if (riskData.success && riskData.riskFactors?.length > 0) {
              console.log(`✅ Loaded ${riskData.riskFactors.length} dynamic risk factors from assessment`);
              riskFactors = riskData.riskFactors.map((factor: any) => ({
                id: factor.id,
                name: factor.name,
                category: factor.category as 'governance' | 'technical_debt' | 'culture' | 'third_party' | 'budget' | 'people',
                currentScore: factor.currentScore,
                weight: factor.weight,
                frequency: factor.frequency,
                impact: factor.impact,
                confidenceLevel: factor.confidenceLevel
              }));
            } else {
              console.warn('⚠️ No assessment risk factors found, using fallback model');
              riskFactors = getFallbackRiskFactors();
            }
          } else {
            console.warn('⚠️ Risk factors API failed, using fallback model');
            riskFactors = getFallbackRiskFactors();
          }
        } catch (error) {
          console.error('❌ Error fetching dynamic risk factors:', error);
          riskFactors = getFallbackRiskFactors();
        }
      } else {
        console.log('⚠️ No assessment ID provided, using fallback risk factors');
        riskFactors = getFallbackRiskFactors();
      }

      // Create risk profile with dynamic or fallback data
      const riskProfile = {
        annualRevenue: inputs.annualRevenue,
        industry: inputs.industry,
        companySize: inputs.annualRevenue > 100000000 ? 'Large' : 'Medium',
        riskFactors
      };

      // Max must accommodate current budget + growth room even if already above industry %
      const budgetConstraints = {
        min: inputs.currentCyberBudget * 1.1,
        max: Math.max(inputs.annualRevenue * 0.08, inputs.currentCyberBudget * 2.5)
      };

      console.log(`💰 CRITICAL: Passing currentCyberBudget to engine: $${(inputs.currentCyberBudget / 1000000).toFixed(2)}M`);
      console.log(`📊 Full inputs state:`, inputs);

      const results = await executiveRiskEngine.runExecutiveSimulation(
        riskProfile,
        inputs.currentCyberBudget,
        budgetConstraints,
        assessmentId // NEW: Pass assessmentId for outside pressures integration
      );

      console.log('✅ Monte Carlo simulation completed:', results);
      setSimulationResults(results);
      
    } catch (error) {
      console.error('❌ Executive simulation failed:', error);
      alert('Simulation failed. Please check the console for details.');
    } finally {
      setIsRunning(false);
    }
  };

  // Card hover effect style
  const cardStyle = {
    background: 'var(--ra-glass-bg)',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    border: '1px solid var(--ra-glass-border)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'default'
  };

  const hoverableCardStyle = {
    ...cardStyle,
    cursor: 'pointer'
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, var(--ra-deep-blue), rgba(0, 102, 255, 0.08))',
      padding: '20px'
    }}>
      
      {/* Header Section with Gradient */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ 
            padding: '14px',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)',
            borderRadius: '16px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}>
            <Calculator size={32} color="white" />
          </div>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              margin: '0 0 5px 0',
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>Financial Impact Analysis <HelpTooltip contentKey="defense.simulation" /></span>
            </h1>
            <p style={{ fontSize: '18px', color: 'var(--ra-text-secondary)', margin: 0 }}>
              Monte Carlo Loss Modeling for Strategic Risk Decisions
            </p>
          </div>
        </div>
        
        {companyData && (
          <div style={{ 
            ...cardStyle,
            background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(0, 102, 255, 0.08) 100%)',
            marginBottom: '20px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '32px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Briefcase aria-hidden="true" size={20} color="white" />
                </div>
                <span style={{ fontWeight: '600', color: 'var(--ra-text-primary)', fontSize: '16px' }}>{companyData.name}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <DollarSign aria-hidden="true" size={20} color="white" />
                </div>
                <span style={{ color: '#a8b2d1' }}>Revenue: <strong>{annualRevenueUnmeasured ? 'Not measured — enter below' : formatCurrency(inputs.annualRevenue)}</strong></span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Shield aria-hidden="true" size={20} color="white" />
                </div>
                <span style={{ color: '#a8b2d1' }}>Security Budget: <strong>{formatCurrency(inputs.currentCyberBudget)}</strong></span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input Controls Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px', 
        marginBottom: '40px',
        maxWidth: '1200px',
        margin: '0 auto 40px'
      }}>
        
        {/* Annual Revenue Input */}
        <div style={cardStyle}
             onMouseEnter={(e) => {
               e.currentTarget.style.transform = 'translateY(-4px)';
               e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
             }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#ccd6f6',
            marginBottom: '10px' 
          }}>
            Annual Revenue ($)
          </label>
          <input
            type="number"
            value={inputs.annualRevenue}
            onChange={(e) => {
              setInputs(prev => ({ ...prev, annualRevenue: Number(e.target.value) || 0 }));
              setAnnualRevenueUnmeasured(false);
            }}
            aria-label="Annual revenue"
            style={{
              width: '100%',
              background: 'var(--ra-deep-blue)',
              border: '2px solid var(--ra-glass-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--ra-text-primary)',
              fontSize: '16px',
              fontWeight: '500',
              transition: 'all 0.2s',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--ra-cyber-aqua)';
              e.target.style.background = 'var(--ra-glass-bg)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--ra-glass-border)';
              e.target.style.background = 'var(--ra-deep-blue)';
            }}
            placeholder="10000000"
          />
        </div>
        
        {/* Cyber Budget Input */}
        <div style={cardStyle}
             onMouseEnter={(e) => {
               e.currentTarget.style.transform = 'translateY(-4px)';
               e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
             }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#ccd6f6',
            marginBottom: '10px' 
          }}>
            Current Cyber Budget ($)
          </label>
          <input
            type="number"
            value={inputs.currentCyberBudget}
            onChange={(e) => setInputs(prev => ({ ...prev, currentCyberBudget: Number(e.target.value) || 0 }))}
            aria-label="Current cyber budget"
            style={{
              width: '100%',
              background: 'var(--ra-deep-blue)',
              border: '2px solid var(--ra-glass-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--ra-text-primary)',
              fontSize: '16px',
              fontWeight: '500',
              transition: 'all 0.2s',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--ra-cyber-aqua)';
              e.target.style.background = 'var(--ra-glass-bg)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--ra-glass-border)';
              e.target.style.background = 'var(--ra-deep-blue)';
            }}
            placeholder="350000"
          />
        </div>
        
        {/* Industry Select */}
        <div style={cardStyle}
             onMouseEnter={(e) => {
               e.currentTarget.style.transform = 'translateY(-4px)';
               e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
             }}
             onMouseLeave={(e) => {
               e.currentTarget.style.transform = 'translateY(0)';
               e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
             }}>
          <label style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#ccd6f6',
            marginBottom: '10px' 
          }}>
            Industry Sector
          </label>
          <select
            value={inputs.industry}
            onChange={(e) => setInputs(prev => ({ ...prev, industry: e.target.value }))}
            aria-label="Industry sector"
            style={{
              width: '100%',
              background: 'var(--ra-deep-blue)',
              border: '2px solid var(--ra-glass-border)',
              borderRadius: '8px',
              padding: '12px',
              color: 'var(--ra-text-primary)',
              fontSize: '16px',
              fontWeight: '500',
              transition: 'all 0.2s',
              outline: 'none',
              cursor: 'pointer'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--ra-cyber-aqua)';
              e.target.style.background = 'var(--ra-glass-bg)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--ra-glass-border)';
              e.target.style.background = 'var(--ra-deep-blue)';
            }}
          >
            <option value="Technology">Technology</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Finance">Finance</option>
            <option value="Retail">Retail</option>
            <option value="Manufacturing">Manufacturing</option>
            <option value="Energy">Energy</option>
            <option value="Transportation and Logistics">Transportation and Logistics</option>
            <option value="Real Estate">Real Estate</option>
            <option value="Hospitality and Tourism">Hospitality and Tourism</option>
            <option value="Education">Education</option>
          </select>
        </div>
        
        {/* Reset to Assessment Values */}
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'flex-end' }}>
          <button
            onClick={() => {
              const measured = companyData?.annualRevenue !== null && companyData?.annualRevenue !== undefined;
              setInputs({
                annualRevenue: measured ? (companyData!.annualRevenue as number) : 0,
                currentCyberBudget: companyData?.currentCyberBudget || 0,
                industry: companyData?.industry || 'General'
              });
              setAnnualRevenueUnmeasured(!measured);
            }}
            style={{
              width: '100%',
              background: 'transparent',
              color: 'var(--ra-text-secondary)',
              fontWeight: '500',
              fontSize: '13px',
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px dashed var(--ra-glass-border)',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ra-cyber-aqua)'; e.currentTarget.style.color = 'var(--ra-cyber-aqua)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ra-glass-border)'; e.currentTarget.style.color = 'var(--ra-text-secondary)'; }}
          >
            Reset to Assessment Values
          </button>
        </div>

        {/* Run Simulation Button */}
        <div style={cardStyle}>
          <button
            onClick={runSimulation}
            disabled={isRunning}
            style={{
              width: '100%',
              background: isRunning 
                ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
                : 'linear-gradient(135deg, #1e3a8a 0%, #3730a3 100%)',
              color: 'white',
              fontWeight: '600',
              fontSize: '16px',
              padding: '15px 20px',
              borderRadius: '8px',
              border: 'none',
              cursor: isRunning ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              opacity: isRunning ? 0.7 : 1,
              transition: 'all 0.3s',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
            onMouseOver={(e) => {
              if (!isRunning) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
          >
            {isRunning ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid white',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Running Analysis...
              </>
            ) : (
              <>
                <Calculator size={20} />
                Run Financial Impact Analysis
              </>
            )}
          </button>
        </div>
      </div>

      {/* Results Display */}
      {simulationResults ? (
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          {/* Current Risk Exposure Card */}
          <div style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(255, 68, 68, 0.08) 100%)',
            borderLeft: '4px solid var(--ra-danger)',
            marginBottom: '24px'
          }}>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'var(--ra-text-primary)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <AlertTriangle aria-hidden="true" size={20} color="white" />
              </div>
              Annualized Loss Exposure (ALE)
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(255, 68, 68, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Expected Annual Loss (ALE)
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-danger)' }}>
                  {formatCurrency(simulationResults.currentState.expectedAnnualLoss)}
                </div>
              </div>

              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(255, 68, 68, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Value at Risk (95th Percentile)
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-danger)' }}>
                  {formatCurrency(simulationResults.currentState.percentile95Loss)}
                </div>
              </div>

              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(255, 68, 68, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Loss Magnitude (% Revenue)
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-danger)' }}>
                  {formatPercent(simulationResults.currentState.percentile95Loss / inputs.annualRevenue)}
                </div>
              </div>
            </div>

            {/* Loss Decomposition */}
            {simulationResults.currentState.lossDecomposition && (
              <div style={{ marginTop: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#ccd6f6', marginBottom: '12px' }}>
                  Loss Decomposition
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px'
                }}>
                  <div style={{
                    background: 'rgba(255, 165, 0, 0.08)',
                    border: '1px solid rgba(255, 165, 0, 0.2)',
                    borderRadius: '8px',
                    padding: '14px'
                  }}>
                    <div style={{ fontSize: '11px', color: '#9a3412', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                      Primary Loss
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#c2410c', marginBottom: '4px' }}>
                      {formatCurrency(simulationResults.currentState.lossDecomposition.primaryLoss.total)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#78350f' }}>
                      Productivity: {formatCurrency(simulationResults.currentState.lossDecomposition.primaryLoss.productivity)} |
                      Response: {formatCurrency(simulationResults.currentState.lossDecomposition.primaryLoss.response)} |
                      Replacement: {formatCurrency(simulationResults.currentState.lossDecomposition.primaryLoss.replacement)}
                    </div>
                  </div>
                  <div style={{
                    background: 'rgba(255, 68, 68, 0.08)',
                    border: '1px solid rgba(255, 68, 68, 0.2)',
                    borderRadius: '8px',
                    padding: '14px'
                  }}>
                    <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>
                      Secondary Loss
                    </div>
                    <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--ra-danger)', marginBottom: '4px' }}>
                      {formatCurrency(simulationResults.currentState.lossDecomposition.secondaryLoss.total)}
                    </div>
                    <div style={{ fontSize: '11px', color: '#7f1d1d' }}>
                      Regulatory: {formatCurrency(simulationResults.currentState.lossDecomposition.secondaryLoss.regulatory)} |
                      Reputation: {formatCurrency(simulationResults.currentState.lossDecomposition.secondaryLoss.reputation)} |
                      Legal: {formatCurrency(simulationResults.currentState.lossDecomposition.secondaryLoss.legal)}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Threat Scenario Charts — Scatter Plot + Loss Exceedance Curve */}
          {simulationResults.scenarioResults && (
            <ThreatScenarioCharts
              scenarioResults={simulationResults.scenarioResults}
              optimizedScenarios={simulationResults.optimizedScenarios}
              recommendedScenario={simulationResults.recommendedScenario}
              formatCurrency={formatCurrency}
            />
          )}

          {/* Recommended Investment Card */}
          <div style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(0, 255, 136, 0.08) 100%)',
            borderLeft: '4px solid var(--ra-success)',
            marginBottom: '24px'
          }}>
            <h2 style={{ 
              fontSize: '24px', 
              fontWeight: 'bold', 
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'var(--ra-text-primary)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <TrendingUp aria-hidden="true" size={20} color="white" />
              </div>
              Recommended Investment Strategy
            </h2>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px' 
            }}>
              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(0, 255, 136, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Additional Investment
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-success)' }}>
                  {formatCurrency(simulationResults.recommendedScenario.budgetIncrease)}
                </div>
              </div>
              
              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(0, 255, 136, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Projected Annual ROI
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-success)' }}>
                  {formatPercent(simulationResults.recommendedScenario.projectedROI)}
                </div>
              </div>
              
              <div style={{
                background: 'var(--ra-glass-bg)',
                border: '1px solid rgba(0, 255, 136, 0.2)',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Payback Period
                </div>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--ra-success)' }}>
                  {simulationResults.recommendedScenario.paybackPeriod.toFixed(1)} months
                </div>
              </div>
            </div>
          </div>

          {/* Executive Summary Card */}
          <div style={{
            ...cardStyle,
            background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(0, 102, 255, 0.08) 100%)',
            borderLeft: '4px solid var(--ra-cyber-aqua)',
            marginBottom: '24px'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: 'var(--ra-text-primary)'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <BarChart3 aria-hidden="true" size={20} color="white" />
              </div>
              Executive Summary
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
              <div>
                <h3 style={{ 
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#ccd6f6',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Activity aria-hidden="true" size={18} color="var(--ra-warning)" />
                  Key Findings
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {simulationResults.boardSummary.keyFindings.slice(0, 3).map((finding, index) => (
                    <li key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '8px',
                      marginBottom: '12px',
                      fontSize: '14px',
                      color: '#a8b2d1',
                      lineHeight: '1.5'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#fbbf24',
                        marginTop: '6px',
                        flexShrink: 0
                      }} />
                      {finding}
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 style={{ 
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#ccd6f6',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Target aria-hidden="true" size={18} color="var(--ra-success)" />
                  Recommendations
                </h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {simulationResults.boardSummary.recommendations.slice(0, 3).map((rec, index) => (
                    <li key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: '8px',
                      marginBottom: '12px',
                      fontSize: '14px',
                      color: '#a8b2d1',
                      lineHeight: '1.5'
                    }}>
                      <div style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: 'var(--ra-success)',
                        marginTop: '6px',
                        flexShrink: 0
                      }} />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Budget Scenarios Comparison */}
          {simulationResults.optimizedScenarios.length > 0 && (
            <div style={{
              ...cardStyle,
              background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(124, 58, 237, 0.08) 100%)',
              borderLeft: '4px solid #7c3aed',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: 'var(--ra-text-primary)'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <DollarSign aria-hidden="true" size={20} color="white" />
                </div>
                Investment Scenarios
              </h2>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--ra-glass-border)' }}>
                      <th style={{ textAlign: 'left', padding: '10px 12px', color: 'var(--ra-text-secondary)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Scenario</th>
                      <th style={{ textAlign: 'right', padding: '10px 12px', color: 'var(--ra-text-secondary)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Investment</th>
                      <th style={{ textAlign: 'right', padding: '10px 12px', color: 'var(--ra-text-secondary)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Risk Reduction</th>
                      <th style={{ textAlign: 'right', padding: '10px 12px', color: 'var(--ra-text-secondary)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>ROI</th>
                      <th style={{ textAlign: 'right', padding: '10px 12px', color: 'var(--ra-text-secondary)', fontWeight: '600', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payback</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simulationResults.optimizedScenarios.map((scenario, idx) => {
                      const isRecommended = scenario.scenarioName === simulationResults.recommendedScenario.scenarioName;
                      return (
                        <tr key={idx} style={{
                          borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
                          background: isRecommended ? 'rgba(0, 255, 136, 0.08)' : 'transparent'
                        }}>
                          <td style={{ padding: '12px', fontWeight: isRecommended ? '700' : '500', color: 'var(--ra-text-primary)' }}>
                            {scenario.scenarioName}
                            {isRecommended && (
                              <span style={{ marginLeft: '8px', fontSize: '11px', background: 'rgba(0, 217, 255, 0.2)', color: '#00d9ff', padding: '2px 8px', borderRadius: '10px', border: '1px solid rgba(0, 217, 255, 0.4)', fontWeight: '600' }}>Recommended</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right', padding: '12px', color: scenario.budgetIncrease >= 0 ? '#ccd6f6' : 'var(--ra-warning)', fontWeight: '500' }}>{scenario.budgetIncrease >= 0 ? '+' : '-'}{formatCurrency(Math.abs(scenario.budgetIncrease))}</td>
                          <td style={{ textAlign: 'right', padding: '12px', color: 'var(--ra-success)', fontWeight: '600' }}>{formatPercent(scenario.projectedRiskReduction)}</td>
                          <td style={{ textAlign: 'right', padding: '12px', color: '#ccd6f6', fontWeight: '500' }}>{formatPercent(scenario.projectedROI)}</td>
                          <td style={{ textAlign: 'right', padding: '12px', color: '#ccd6f6' }}>{scenario.paybackPeriod.toFixed(1)} mo</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Generate Board Report Button */}
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <button
              onClick={() => setShowBoardReport(true)}
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '14px 28px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                transition: 'all 0.3s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
              }}
            >
              <FileText aria-hidden="true" size={20} />
              Generate Board Report
            </button>
          </div>

        </div>
      ) : (
        /* Ready State Card */
        <div style={{ 
          ...cardStyle,
          maxWidth: '600px',
          margin: '0 auto',
          textAlign: 'center',
          background: 'linear-gradient(135deg, var(--ra-glass-bg) 0%, rgba(0, 102, 255, 0.08) 100%)'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}>
            <Activity aria-hidden="true" size={40} color="white" />
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px', color: 'var(--ra-text-primary)' }}>
            Ready for Financial Impact Analysis
          </h2>
          <p style={{ fontSize: '16px', color: 'var(--ra-text-secondary)', margin: 0, lineHeight: '1.6' }}>
            Click "Run Financial Impact Analysis" to calculate Annualized Loss Exposure using
            10,000 Monte Carlo iterations with loss decomposition and budget optimization.
          </p>
        </div>
      )}

      {/* Board Report Modal */}
      {showBoardReport && simulationResults && (
        <BoardReportGenerator
          simulationResults={simulationResults}
          companyData={{
            name: companyData?.name || 'Sample Corporation',
            industry: inputs.industry,
            annualRevenue: inputs.annualRevenue,
            currentCyberBudget: inputs.currentCyberBudget
          }}
          outsidePressures={outsidePressures}
          prismData={prismData}
          crsScore={crsScore}
          onClose={() => setShowBoardReport(false)}
        />
      )}

      {/* Add keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ExecutiveSimulationStudioSimple;